import React, { useState } from "react";
import axios from "axios";
import { Button } from "../../../components/ui/button";
import { Star, Trash2, ArrowUp, ArrowDown, RotateCw, Crop } from "lucide-react";
import { CropImageModal } from "./CropImageModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * Gallery of EXISTING article images with controls to:
 *  - ⭐ Set any image as the PRIMARY (moves it to index 0 → becomes the
 *    product thumbnail on the site + main image on eBay).
 *  - ⬆️/⬇️ Reorder one position up/down.
 *  - 🗑️ Remove from the list (drops the URL; the file stays on R2 for safety).
 *
 * Persists each change via PUT /api/articles/{id}/reorder-images, which also
 * re-syncs the listing to eBay so the main image follows on the marketplace.
 */
export const ExistingImagesGallery = ({ articleId, images, onUpdated }) => {
  const [order, setOrder] = useState(images || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!order.length) return null;

  const persist = async (newOrder) => {
    setSaving(true);
    setError(null);
    try {
      const token = sessionStorage.getItem("access_token");
      const res = await axios.put(
        `${API}/articles/${articleId}/reorder-images`,
        { images_url: newOrder },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const persisted = res.data?.images_url || newOrder;
      setOrder(persisted);
      if (typeof onUpdated === "function") onUpdated(persisted);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      // Revert UI on failure
      setOrder(images);
    } finally {
      setSaving(false);
    }
  };

  const setPrimary = (idx) => {
    if (idx === 0) return;
    const next = [order[idx], ...order.slice(0, idx), ...order.slice(idx + 1)];
    persist(next);
  };

  const move = (idx, delta) => {
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= order.length) return;
    const next = [...order];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    persist(next);
  };

  const [cropIdx, setCropIdx] = useState(null);

  const whiten = async (idx) => {
    if (!window.confirm("Rendere lo sfondo di questa foto bianco pulito (stile eBay)? La foto originale resta archiviata.")) return;
    setSaving(true);
    setError(null);
    try {
      const token = sessionStorage.getItem("access_token");
      const res = await axios.post(`${API}/articles/${articleId}/white-background`, { index: idx }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const persisted = res.data?.images_url || order;
      setOrder(persisted);
      if (typeof onUpdated === "function") onUpdated(persisted);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const rotate = async (idx) => {
    setSaving(true);
    setError(null);
    try {
      const token = sessionStorage.getItem("access_token");
      const res = await axios.post(
        `${API}/articles/${articleId}/rotate-image`,
        { index: idx, degrees: 90 },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const persisted = res.data?.images_url || order;
      setOrder(persisted);
      if (typeof onUpdated === "function") onUpdated(persisted);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = (idx) => {
    if (order.length <= 1) {
      setError("Almeno una foto deve restare. Caricane prima un'altra.");
      return;
    }
    if (!window.confirm("Rimuovere questa foto dall'articolo?")) return;
    const next = order.filter((_, i) => i !== idx);
    persist(next);
  };

  return (
    <div className="mt-3 border border-gray-200 rounded-lg p-3 bg-gray-50" data-testid="existing-images-gallery">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
          Foto caricate ({order.length})
        </h4>
        {saving && <span className="text-xs text-blue-600">salvataggio…</span>}
      </div>
      {error && (
        <div className="mb-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
          {error}
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {order.map((url, idx) => (
          <div
            key={url}
            className={`relative rounded-md border-2 bg-white overflow-hidden ${
              idx === 0 ? "border-amber-400 ring-2 ring-amber-200" : "border-gray-200"
            }`}
            data-testid={`gallery-image-${idx}`}
          >
            <div className="aspect-square">
              <img
                src={url}
                alt={`Foto ${idx + 1}`}
                className="w-full h-full object-contain"
                loading="lazy"
              />
            </div>
            {idx === 0 && (
              <span className="absolute top-1 left-1 text-[10px] font-bold bg-amber-400 text-white px-1.5 py-0.5 rounded">
                ⭐ Principale
              </span>
            )}
            <div className="absolute bottom-1 right-1 flex gap-1">
              {idx > 0 && (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => setPrimary(idx)}
                  className="bg-amber-500 hover:bg-amber-600 text-white p-1 rounded text-[10px] shadow"
                  title="Imposta come foto principale"
                  data-testid={`set-primary-${idx}`}
                >
                  <Star className="w-3 h-3" />
                </button>
              )}
              <button
                type="button"
                disabled={saving || idx === 0}
                onClick={() => move(idx, -1)}
                className="bg-gray-600 hover:bg-gray-700 text-white p-1 rounded text-[10px] shadow disabled:opacity-30"
                title="Sposta sinistra"
                data-testid={`move-up-${idx}`}
              >
                <ArrowUp className="w-3 h-3" />
              </button>
              <button
                type="button"
                disabled={saving || idx === order.length - 1}
                onClick={() => move(idx, 1)}
                className="bg-gray-600 hover:bg-gray-700 text-white p-1 rounded text-[10px] shadow disabled:opacity-30"
                title="Sposta destra"
                data-testid={`move-down-${idx}`}
              >
                <ArrowDown className="w-3 h-3" />
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => whiten(idx)}
                className="bg-white hover:bg-gray-100 text-gray-800 border border-gray-300 p-1 rounded text-[10px] shadow disabled:opacity-50 font-bold leading-none"
                title="Sfondo bianco pulito (stile eBay)"
                data-testid={`whiten-image-${idx}`}
              >
                ⬜
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => setCropIdx(idx)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white p-1 rounded text-[10px] shadow disabled:opacity-50"
                title="Ritaglia / centra il modellino"
                data-testid={`crop-image-${idx}`}
              >
                <Crop className="w-3 h-3" />
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => rotate(idx)}
                className="bg-sky-600 hover:bg-sky-700 text-white p-1 rounded text-[10px] shadow disabled:opacity-50"
                title="Ruota di 90° in senso orario"
                data-testid={`rotate-image-${idx}`}
              >
                <RotateCw className="w-3 h-3" />
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => remove(idx)}
                className="bg-red-600 hover:bg-red-700 text-white p-1 rounded text-[10px] shadow"
                title="Rimuovi foto"
                data-testid={`delete-image-${idx}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-gray-500 mt-2">
        💡 Clicca ⭐ per impostare una foto come principale (verrà mostrata per prima sul sito e su eBay). ↻ ruota la foto di 90°, ✂ ritaglia/centra il modellino, ⬜ sfondo bianco.
      </p>
      {cropIdx !== null && (
        <CropImageModal
          articleId={articleId}
          index={cropIdx}
          imageUrl={order[cropIdx]}
          onClose={() => setCropIdx(null)}
          onDone={(persisted) => {
            setCropIdx(null);
            setOrder(persisted);
            if (typeof onUpdated === "function") onUpdated(persisted);
          }}
        />
      )}
    </div>
  );
};
